/**
 * Reviewer Mode Toggle Component
 *
 * A toggle switch to enable/disable reviewer mode.
 * Supports compact mode for inline placement.
 */
import { Eye, GraduationCap } from 'lucide-react';

import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

import { useReadOnlyMode } from '../hooks/useReadOnlyMode';
import { ReadOnlyToggleProps } from '../types';

export const ReadOnlyToggle = ({ className, compact = false }: ReadOnlyToggleProps) => {
  const { isReadOnly, setReadOnlyMode } = useReadOnlyMode();

  if (compact) {
    return (
      <div className={cn('flex items-center gap-1.5', className)}>
        {isReadOnly ? (
          <Eye className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <GraduationCap className="h-3.5 w-3.5 text-muted-foreground" />
        )}
        <Label
          htmlFor="read-only-toggle-compact"
          className="hidden cursor-pointer font-body text-xs text-muted-foreground sm:inline"
        >
          {isReadOnly ? 'Reviewer' : 'Student'}
        </Label>
        <Switch
          id="read-only-toggle-compact"
          checked={isReadOnly}
          onCheckedChange={setReadOnlyMode}
          className="scale-75 data-[state=checked]:bg-livelinx-purple200"
        />
      </div>
    );
  }

  return (
    <div
      className={cn('flex items-center gap-2 rounded-lg bg-livelinx-purple50 px-3 py-2', className)}
    >
      {isReadOnly ? (
        <Eye className="h-4 w-4 text-livelinx-purple200" />
      ) : (
        <GraduationCap className="h-4 w-4 text-livelinx-purple200" />
      )}
      <Label
        htmlFor="read-only-toggle"
        className="cursor-pointer font-body text-sm text-livelinx-purple200"
      >
        {isReadOnly ? 'Reviewer' : 'Student'}
      </Label>
      <Switch
        id="read-only-toggle"
        checked={isReadOnly}
        onCheckedChange={setReadOnlyMode}
        className="data-[state=checked]:bg-livelinx-purple200"
      />
    </div>
  );
};
